/**
 * "Shop the look" drawer (snippets/shop-the-look-drawer.liquid).
 *
 * Una página puede traer looks de tres orígenes distintos, y cada uno pide su propio
 * contenido. El chrome es el mismo para los tres; lo que cambia es de dónde sale el
 * contenido, y lo decide la tarjeta que abre con su data-stl-mode:
 *
 *   · "look" — plantilla collection.shop-the-look.json: el look lo define un bloque de
 *     sections/shop-the-look-grid.liquid, con sus propias fotos y su propia lista de
 *     productos. El drawer enseña las fotos del look y sus productos como parrilla de
 *     cards con quick-add.
 *     → una petición por producto a sections/stl-look-product.liquid
 *   · "cards" — modo `shop_the_look` de la parrilla de colección: cada producto con
 *     complementarios ES un look, y el drawer lo enseña como parrilla de cards, el
 *     mismo final que el paso 2 del drawer "Crear tu look" de la PDP.
 *     → sections/shop-the-look-cards.liquid
 *   · "buy" — looks de metaobject `shop_the_look` (tarjeta colocada en la parrilla y
 *     sección banner): el drawer enseña una PDP reducida del producto —tallas, precio,
 *     añadir a la cesta— con una tira para cambiar de pieza del look.
 *     → sections/shop-the-look-content.liquid
 *
 * "cards" y "buy" tienen producto ancla, así que el look entero viaja en una sola
 * respuesta pedida bajo la URL de ese producto. "look" no lo tiene —los productos los
 * decide el bloque y solo se conocen aquí—, así que se arma por partes: las fotos salen
 * de la propia tarjeta (ya están descargadas) y cada producto se pide por separado, en
 * paralelo y cacheado por handle, que es lo que hace que un producto repetido entre
 * looks no se vuelva a pedir.
 *
 * En los tres casos el contenido montado se cachea por modo + look, así que reabrir uno
 * es instantáneo. Con la página solo viaja el chrome, que es lo que permite empezar a
 * abrir el panel antes de que llegue el fetch.
 *
 * Quick-add and wishlist inside the injected cards need no wiring here: both listen on
 * document (assets/quick-add-cart.js, assets/wishlist.js), so injected markup is live.
 *
 * La única excepción es la sheet de tallas de móvil, que es la MISMA en las dos variantes:
 * el bloque de compra de snippets/stl-buy-block.liquid. En "buy" el panel entero ES esa
 * sheet; en "cards" se monta encima del panel cuando se pulsa el "+" de una card, porque
 * quick-add-cart.js le cede el paso (ver openLookSheet allí) en lugar de abrir la global.
 * El alta la hace en los dos casos aquel módulo (window.quickAddCart.addToCart), así que
 * el carrito, el contador de la cabecera y el minicart responden igual que ante cualquier
 * otra card del sitio.
 */
(function () {
  const BODY_OPEN_CLASS = 'stl-open';
  // El de las tarjetas sin data-stl-mode: los looks de metaobject son los que
  // comparten el drawer con el banner y con la parrilla.
  const DEFAULT_MODE = 'buy';
  // Presentación del panel por modo: "look" y "cards" se ven igual (parrilla de
  // cards), así que comparten el valor con el que el CSS los distingue de "buy".
  const LAYOUTS = { buy: 'buy', cards: 'cards', look: 'cards' };
  // Mirrors the .stl-media column cap in scss/components/_shop-the-look.scss, so the
  // candidate the browser picks when warming an image is the same one selectThumb asks
  // for later — otherwise the warm-up downloads a file the click never reuses.
  const MEDIA_SIZES = '415px';
  // Marca el <body> mientras la sheet está montada sobre el panel de cards: de ahí
  // cuelga en el CSS la elevación de la guía de tallas.
  const SHEET_OPEN_CLASS = 'stl-sheet-open';
  // Id propio para la guía que se monta desde aquí: el que trae la PDP es el de
  // su bloque, y es al que apuntan los <modal-opener> de los bloques de compra.
  // Uno solo porque solo hay una guía viva a la vez (ver clearSizeGuides).
  const GUIDE_MODAL_ID = 'stl-size-guide';
  // Recorrido hacia abajo a partir del cual soltar la sheet la cierra; el mismo
  // que la sheet global de quick-add (DRAG_CLOSE_PX en assets/quick-add-cart.js).
  const DRAG_CLOSE_PX = 80;
  // Lo que tiene que durar el ratón sobre una tarjeta para precargar su look:
  // por debajo, barrer la parrilla con el cursor pediría todos los que cruza.
  const INTENT_DELAY_MS = 80;
  // Tope de espera de la foto antes de fundir la columna de media: con una red
  // lenta se enseña igual, en lugar de dejar el hueco vacío indefinidamente.
  const MEDIA_REVEAL_TIMEOUT_MS = 1500;

  class ShopTheLookDrawer extends HTMLElement {
    connectedCallback() {
      // Un solo drawer por página. La parrilla de colección emite el suyo y la
      // sección banner (sections/shop-the-look-banner.liquid) el suyo, y ambos
      // escuchan en document: con dos activos, un click en una tarjeta abriría
      // los dos paneles superpuestos. El chrome es idéntico, así que el segundo
      // sobra y se retira.
      const active = document.querySelector('shop-the-look-drawer[data-stl-active]');
      if (active && active !== this) {
        this.standby = true;
        this.remove();
        return;
      }
      this.setAttribute('data-stl-active', '');

      this.drawer = this.querySelector('.stl__drawer');
      // titleEl y no title: `title` es propiedad nativa de HTMLElement y
      // asignarle un nodo dejaría un tooltip en el panel entero.
      this.titleEl = this.querySelector('[data-stl-title]');
      this.defaultTitle = this.titleEl ? this.titleEl.textContent : '';
      this.mediaSlot = this.querySelector('[data-stl-media-slot]');
      this.body = this.querySelector('[data-stl-body]');
      this.loader = this.querySelector('.stl__loader');
      this.sheet = this.querySelector('[data-stl-sheet]');
      this.sheetContent = this.querySelector('[data-stl-sheet-content]');
      // Una sección por variante de drawer; ver la cabecera del fichero. La de "look"
      // no trae el look entero sino UN producto suyo: se pide una vez por pieza.
      this.sectionIds = {
        buy: this.dataset.sectionId || 'shop-the-look-content',
        cards: this.dataset.cardsSectionId || 'shop-the-look-cards',
        look: this.dataset.lookSectionId || 'stl-look-product',
      };

      // `${mode}:${handle}` -> parsed [data-stl-content] element, reused on re-open.
      // El modo va en la clave porque el mismo producto tiene contenido distinto
      // en cada variante: reutilizar el de la otra pintaría el drawer que no es.
      this.cache = new Map();
      // Misma clave -> petición en vuelo. La comparten la precarga por hover y la
      // apertura, para que un click durante la precarga no pida el look otra vez.
      this.pending = new Map();
      // Solo el modo "look": handle -> [data-stl-product-payload] parseado, y su
      // petición en vuelo. Van por handle y no por look a propósito, que es lo que
      // hace que una prenda que sale en varios looks se pida una sola vez.
      this.productCache = new Map();
      this.productPending = new Map();
      // handle -> HTML de la PDP, para la guía de tallas.
      this.sizeGuideCache = new Map();
      this.key = null;
      this.opener = null;

      this.onDocClick = this.onDocClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerOver = this.onPointerOver.bind(this);
      this.onIntent = this.onIntent.bind(this);

      document.addEventListener('click', this.onDocClick);
      // Las tarjetas de look se colocan en la parrilla desde JS (placeBanners) y
      // el banner es otra sección: en document las cubre a todas.
      document.addEventListener('pointerover', this.onIntent);
      document.addEventListener('focusin', this.onIntent);
      this.addEventListener('change', this.onChange);
      document.addEventListener('keydown', this.onKeydown);
      // Pointer-over rather than pointer-enter: it bubbles, so one listener covers thumbs
      // that are injected long after this.
      this.addEventListener('pointerover', this.onPointerOver);
      this.addEventListener('focusin', this.onPointerOver);

      this.initDragToClose();
    }

    disconnectedCallback() {
      if (this.standby) return;
      document.removeEventListener('click', this.onDocClick);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('pointerover', this.onIntent);
      document.removeEventListener('focusin', this.onIntent);
      clearTimeout(this.intentTimer);
      document.body.classList.remove(SHEET_OPEN_CLASS);
    }

    /**
     * Precarga del look en cuanto el ratón se queda sobre su tarjeta: entre el
     * hover y el click suele haber unos cientos de ms, y es lo que tarda la
     * sección. Así la primera apertura ya no espera ni al fetch ni a la foto.
     * El toque no cuenta: en móvil llega pegado al click, y también al empezar
     * un scroll, que precargaría cada tarjeta que se cruza.
     */
    onIntent(e) {
      if (e.pointerType === 'touch') return;
      const opener = e.target.closest?.('[data-stl-open]') || null;
      if (opener === this.intentOpener) return;

      clearTimeout(this.intentTimer);
      this.intentOpener = opener;
      if (opener) this.intentTimer = setTimeout(() => this.prefetch(opener), INTENT_DELAY_MS);
    }

    prefetch(opener) {
      const handle = opener.dataset.stlOpen;
      if (!handle) return;
      const mode = this.modeFor(opener);
      const key = this.keyFor(handle, mode);
      if (this.cache.has(key) || this.pending.has(key)) return;

      this.contentFor(opener, handle, mode)
        .then((content) => this.warmMainImage(content, opener))
        // Si falla, el click lo reintenta y es él quien gestiona el error.
        .catch(() => {});
    }

    onDocClick(e) {
      const opener = e.target.closest('[data-stl-open]');
      // The wishlist button lives inside the card; let its own handler have the click.
      if (opener && !e.target.closest('.brn-card__actions')) {
        e.preventDefault();
        // Clicking a specific product's thumbnail on the card opens the drawer on
        // that product; clicking anywhere else falls back to the first of the look.
        const picked = e.target.closest('[data-stl-product]');
        this.open(opener, picked?.dataset.stlProduct || null);
        return;
      }

      if (!this.hasAttribute('open')) return;

      const selector = e.target.closest('[data-stl-select]');
      if (selector && this.contains(selector)) {
        e.preventDefault();
        this.selectProduct(selector.dataset.stlSelect);
        return;
      }

      const add = e.target.closest('[data-stl-add]');
      if (add && this.contains(add)) {
        e.preventDefault();
        this.addToCart(add.dataset.stlVariant, add.closest('[data-stl-buy]')?.dataset.productId);
        return;
      }

      const guideOpener = e.target.closest('[data-stl-size-guide-open]');
      if (guideOpener && this.contains(guideOpener)) {
        this.openSizeGuide(guideOpener);
        return;
      }

      // Antes que el aspa del panel: en "cards" la sheet se monta encima y se
      // cierra sola, dejando el look abierto detrás.
      const sheetCloser = e.target.closest('[data-stl-sheet-close]');
      if (sheetCloser && this.contains(sheetCloser)) {
        e.preventDefault();
        this.closeSheet();
        return;
      }

      const closer = e.target.closest('[data-stl-close]');
      if (closer && this.contains(closer)) {
        e.preventDefault();
        this.close();
        return;
      }

      const thumb = e.target.closest('[data-stl-thumb]');
      if (thumb && this.contains(thumb)) {
        e.preventDefault();
        this.selectThumb(thumb);
      }
    }

    onKeydown(e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      if (!this.hasAttribute('open')) return;

      // Una capa por pulsación: la sheet primero, el look después.
      if (this.sheetIsOpen()) this.closeSheet();
      else this.close();
    }

    initDragToClose() {
      const drawer = this.drawer;
      if (!drawer) return;

      let startY = 0;
      let armed = false;
      let dragging = false;

      drawer.addEventListener(
        'touchstart',
        (e) => {
          armed =
            this.hasAttribute('open') && this.getAttribute('data-stl-mode') === 'buy' && this.isMobile();
          dragging = false;
          if (armed) startY = e.touches[0].clientY;
        },
        { passive: true }
      );

      drawer.addEventListener(
        'touchmove',
        (e) => {
          if (!armed) return;
          const dy = e.touches[0].clientY - startY;

          if (!dragging) {
            const atTop = !this.body || this.body.scrollTop <= 0;
            if (!atTop || dy <= 8 || !e.cancelable) return;
            dragging = true;
            drawer.style.transition = 'none';
          }

          e.preventDefault();
          drawer.style.transform = `translateY(${Math.max(0, dy)}px)`;
        },
        { passive: false }
      );

      const endDrag = (e) => {
        armed = false;
        if (!dragging) return;
        dragging = false;

        const dy = (e.changedTouches?.[0]?.clientY ?? startY) - startY;
        drawer.style.transition = '';
        drawer.style.transform = '';
        if (dy > DRAG_CLOSE_PX) this.close();
      };

      drawer.addEventListener('touchend', endDrag);
      drawer.addEventListener('touchcancel', endDrag);
    }

    open(opener, preselect = null) {
      const handle = opener.dataset.stlOpen;
      if (!handle) return;

      // Lo decide la tarjeta, no la página: una misma colección puede llevar a
      // la vez tarjetas de la plantilla shop-the-look (modo cards) y looks de
      // metaobject colocados en la parrilla (modo buy).
      const mode = this.modeFor(opener);
      // Marca el panel: de esto cuelga en el CSS la presentación de cada
      // variante (en móvil, sheet por abajo en "buy" y panel entero por la
      // derecha en "cards"). Solo hay DOS presentaciones para tres modos: un look
      // de sección se ve exactamente igual que uno de complementarios —parrilla de
      // cards— y lo que los separa es de dónde sale el contenido, no cómo se ve.
      const layout = LAYOUTS[mode] || LAYOUTS[DEFAULT_MODE];
      const modeChanged = this.getAttribute('data-stl-mode') !== layout;
      this.setAttribute('data-stl-mode', layout);
      if (modeChanged) {
        // Cada variante entra desde un lado distinto. Cambiando el modo y
        // abriendo en el mismo fotograma, la transición arrancaría desde la
        // posición cerrada del modo ANTERIOR y el panel entraría en diagonal;
        // esta lectura fuerza el recálculo para que salga del sitio correcto.
        // Se guarda porque una expresión suelta la barre el linter.
        this.reflow = this.drawer?.offsetHeight;
      }

      // "Look de N artículos": el recuento lo trae la tarjeta, que es la única
      // que lo sabe (ver snippets/shop-the-look-drawer.liquid). Se pone aquí y
      // no en render() para que el rótulo entre ya completo con el panel, en
      // vez de cambiar a media transición cuando aterrice el contenido.
      if (this.titleEl) this.titleEl.textContent = opener.dataset.stlTitle || this.defaultTitle;

      // Applied after render(); the content may still be in flight.
      this.preselect = preselect;
      this.opener = opener;
      this.setAttribute('open', '');
      document.body.classList.add(BODY_OPEN_CLASS);
      if (this.drawer) {
        this.drawer.scrollTop = 0;
        this.drawer.focus();
      }
      this.load(opener, handle, mode);
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.closeSheet();
      // La guía inyectada se muda al <body> y sobreviviría al cierre: fuera de
      // aquí la resolvería como suya la sheet global de quick-add, que busca
      // .size-chart con document.querySelector.
      this.clearSizeGuides();
      this.removeAttribute('open');
      document.body.classList.remove(BODY_OPEN_CLASS);
      // Return focus to the card that opened it, so keyboard users keep their place.
      this.opener?.querySelector('a.brn-card')?.focus();
      this.opener = null;
    }

    async load(opener, handle, mode = DEFAULT_MODE) {
      // Look y variante: la misma tarjeta abierta en el otro modo es otro
      // contenido, así que no puede compartir ni el early return ni la caché.
      const key = this.keyFor(handle, mode);

      // Reabrir el MISMO look no vuelve a pedir nada —el marcado sigue montado—,
      // pero sí tiene que rehacer la selección: el [data-stl-current] se quedó
      // donde lo dejó la apertura anterior, así que sin esto pulsar otra
      // miniatura reabría el drawer en el producto de la vez anterior.
      // Sin miniatura (click en el resto de la tarjeta) vuelve al primero del
      // look, que es lo que muestra Liquid en una apertura en frío.
      // Si la petición anterior sigue en vuelo todavía no hay nada que marcar:
      // render() aplicará this.preselect en cuanto llegue.
      if (this.key === key) {
        const target = this.preselect || this.firstProductHandle();
        if (target) this.selectProduct(target);
        return;
      }

      const cached = this.cache.get(key);
      if (cached) {
        this.key = key;
        this.render(cached);
        return;
      }

      this.key = key;
      this.classList.add('is-loading');

      try {
        const content = await this.contentFor(opener, handle, mode);
        // A slow fetch can land after the user has moved on to another look.
        if (this.key === key) this.render(content);
      } catch (error) {
        if (this.key !== key) return;
        console.error('[shop-the-look] could not load look', handle, error);
        // Fall back to the behaviour the card would have had without this drawer:
        // send the visitor to the product page rather than leave an empty panel.
        const href = this.opener?.querySelector('a.brn-card')?.href;
        this.key = null;
        this.classList.remove('is-loading');
        this.close();
        if (href) window.location.href = href;
      }
    }

    /**
     * El contenido de un look, venga de donde venga. Es el único punto en el que se
     * separan las dos maneras de conseguirlo, para que la apertura y la precarga no
     * tengan que saber cuál es cuál: las dos ramas devuelven el mismo
     * [data-stl-content] que espera render().
     */
    contentFor(opener, handle, mode) {
      if (mode !== 'look') return this.fetchContent(handle, mode);

      return this.fetchLook(opener).then((content) => {
        // La columna solo se monta cuando se va a pintar, así que un look que se
        // abrió antes en móvil la tiene pendiente: se le añade la primera vez que
        // se abre con la columna a la vista (un giro de pantalla, un resize).
        if (!content.querySelector('[data-stl-media]')) {
          const media = this.lookMedia(opener);
          if (media) content.prepend(media);
        }
        return content;
      });
    }

    /**
     * Un look de sección: una petición por producto, en paralelo, y el panel montado
     * aquí con lo que llegue. Lo que falle se cae del look en vez de tumbarlo entero
     * —un producto despublicado no debería dejar la tarjeta sin drawer—, así que solo
     * se rechaza cuando no queda nada que enseñar.
     */
    fetchLook(opener) {
      const key = this.keyFor(opener.dataset.stlOpen, 'look');
      const cached = this.cache.get(key);
      if (cached) return Promise.resolve(cached);

      let pending = this.pending.get(key);
      if (!pending) {
        const handles = (opener.dataset.stlProducts || '')
          .split(',')
          .map((handle) => handle.trim())
          .filter(Boolean);

        pending = Promise.all(handles.map((handle) => this.fetchProduct(handle).catch(() => null)))
          .then((parts) => {
            const content = this.assembleLook(opener, parts);
            if (!content) throw new Error('empty');
            this.cache.set(key, content);
            return content;
          })
          .finally(() => this.pending.delete(key));
        this.pending.set(key, pending);
      }
      return pending;
    }

    /**
     * Una pieza suelta del look (sections/stl-look-product.liquid): la card de
     * catálogo y su bloque de compra. Se cachea por handle, no por look, porque la
     * misma prenda suele repetirse entre looks de la misma página.
     */
    fetchProduct(handle) {
      const cached = this.productCache.get(handle);
      if (cached) return Promise.resolve(cached);

      let pending = this.productPending.get(handle);
      if (!pending) {
        pending = fetch(`${this.rootBase()}products/${handle}?section_id=${this.sectionIds.look}`)
          .then(async (response) => {
            if (!response.ok) throw new Error(`${response.status}`);
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const payload = doc.querySelector('[data-stl-product-payload]');
            if (!payload) throw new Error('empty');
            this.productCache.set(handle, payload);
            return payload;
          })
          .finally(() => this.productPending.delete(handle));
        this.productPending.set(handle, pending);
      }
      return pending;
    }

    /**
     * Arma con las piezas el mismo [data-stl-content] que devuelven las otras dos
     * variantes, para que de aquí en adelante todo —render, la sheet de talla, la
     * guía— siga siendo el mismo camino. Las piezas se clonan: las respuestas se
     * quedan cacheadas y tienen que seguir intactas para el siguiente look que las
     * use.
     */
    assembleLook(opener, parts) {
      const grid = document.createElement('ul');
      grid.className = 'stl-grid';
      grid.setAttribute('data-stl-grid', '');
      grid.setAttribute('role', 'list');

      const buys = document.createElement('div');
      buys.setAttribute('data-stl-buy-blocks', '');

      let first = true;
      parts.forEach((payload) => {
        if (!payload) return;
        const card = payload.querySelector('[data-stl-card] > li');
        if (card) grid.append(card.cloneNode(true));

        const buy = payload.querySelector('[data-stl-buy-wrap] > [data-stl-buy]');
        if (buy) {
          const copy = buy.cloneNode(true);
          // El primero del look es el que las otras variantes marcan desde Liquid
          // con index 0; aquí el orden lo fija el bloque, no la respuesta, así que
          // se marca al montar.
          if (first) copy.setAttribute('data-stl-current', '');
          buys.append(copy);
        }
        first = false;
      });

      if (!grid.children.length) return null;

      const content = document.createElement('div');
      content.setAttribute('data-stl-content', '');
      const media = this.lookMedia(opener);
      if (media) content.append(media);
      content.append(grid, buys);
      return content;
    }

    /**
     * La columna de media de un look de sección. NO es el carrusel de la tarjeta:
     * dentro del drawer la media se comporta como en las otras dos variantes —foto
     * grande y tira de miniaturas, y la tira es la que cambia la foto—, así que la
     * tarjeta la trae ya montada en un <template> con ese mismo marcado
     * (snippets/look-grid-card.liquid). De ahí que aquí solo haya que clonarlo: de
     * selectThumb en adelante el camino es el de siempre.
     *
     * No se monta si la columna no se pinta (móvil, donde es display:none): clonar
     * allí solo pondría a reproducir vídeos que nadie ve.
     */
    lookMedia(opener) {
      if (!this.mediaSlot?.getClientRects().length) return null;

      const template = opener.querySelector('template[data-stl-look-media]');
      if (!template) return null;

      return template.content.cloneNode(true).querySelector('[data-stl-media]');
    }

    /**
     * El contenido parseado de un look: de la caché, de la petición que ya esté
     * en vuelo o de una nueva. Solo cachea lo que llega bien; un fallo se olvida
     * para que la siguiente apertura vuelva a intentarlo.
     */
    fetchContent(handle, mode) {
      const key = this.keyFor(handle, mode);
      const cached = this.cache.get(key);
      if (cached) return Promise.resolve(cached);

      let pending = this.pending.get(key);
      if (!pending) {
        const sectionId = this.sectionIds[mode] || this.sectionIds[DEFAULT_MODE];
        pending = fetch(`${this.rootBase()}products/${handle}?section_id=${sectionId}`)
          .then(async (response) => {
            if (!response.ok) throw new Error(`${response.status}`);
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const content = doc.querySelector('[data-stl-content]');
            if (!content) throw new Error('empty');
            this.cache.set(key, content);
            return content;
          })
          .finally(() => this.pending.delete(key));
        this.pending.set(key, pending);
      }
      return pending;
    }

    // Lo decide la tarjeta, no la página; ver open().
    modeFor(opener) {
      return this.sectionIds[opener.dataset.stlMode] ? opener.dataset.stlMode : DEFAULT_MODE;
    }

    // El modo va en la clave porque el mismo producto tiene contenido distinto
    // en cada variante.
    keyFor(handle, mode) {
      return `${mode}:${handle}`;
    }

    /**
     * Splits the fetched content across the two slots. A clone is injected so the cached
     * copy stays pristine for the next open.
     */
    render(content) {
      // La guía del look anterior vive ya en <body> y no se va con el marcado que
      // se reemplaza aquí. Importa sobre todo al saltar a un drawer de cards: su
      // quick-add resuelve .size-chart con document.querySelector y se llevaría
      // la tabla que dejó colgada el bloque de compra.
      this.closeSheet();
      this.clearSizeGuides();

      const fragment = content.cloneNode(true);
      const media = fragment.querySelector('[data-stl-media]');
      const grid = fragment.querySelector('[data-stl-grid]');
      // Solo la variante de cards manda los bloques por separado: en "buy" son el
      // contenido del panel y llegan ya dentro de [data-stl-grid].
      const buys = fragment.querySelector('[data-stl-buy-blocks]');

      // La foto que se va a ver, pedida ya y no en diferido. Solo si la columna
      // se pinta: en móvil es display:none y el lazy es lo que evita bajarla.
      const mediaShown = !!this.mediaSlot?.getClientRects().length;
      const mainImage = media && mediaShown ? this.mainImage(media, this.preselect) : null;
      if (mainImage) mainImage.loading = 'eager';

      if (this.mediaSlot) this.mediaSlot.replaceChildren(...(media ? [media] : []));
      if (media) this.revealMedia(media, mainImage);
      if (this.body) {
        this.body.replaceChildren(...(grid ? [grid] : []));
        if (this.loader) this.body.prepend(this.loader);
      }
      if (this.sheetContent) {
        this.sheetContent.replaceChildren(...(buys ? Array.from(buys.childNodes) : []));
      }

      this.classList.remove('is-loading');
      // El corazón del bloque de compra llega después de que wishlist.js marcase los
      // de la página: sin esto saldría vacío aunque el producto ya esté en la lista.
      window.wishlist?.updateAllButtons?.();
      this.warmMedia();
      // Sin preselección el marcado por defecto lo trae Liquid (index 0), pero
      // su guía de tallas hay que pedirla igualmente. En modo cards no hay
      // bloques de compra ni miniaturas que preseleccionen: no hay nada que
      // marcar y esto se queda a cero.
      const current =
        this.preselect ||
        this.querySelector('[data-stl-buy][data-stl-current]')?.dataset.productHandle;
      if (current) this.selectProduct(current);
    }

    /**
     * La columna de media se funde cuando su foto ya está decodificada, no al
     * montarse: con la animación arrancando en la inserción, en la primera
     * apertura se fundía un marco vacío y la foto aparecía de golpe después, al
     * terminar de bajar. Con la foto en caché, decode() resuelve casi en el acto.
     */
    async revealMedia(media, image) {
      if (image) {
        await Promise.race([
          // Una imagen rota rechaza: se enseña igual la columna, con la tira.
          image.decode().catch(() => {}),
          new Promise((resolve) => {
            setTimeout(resolve, MEDIA_REVEAL_TIMEOUT_MS);
          }),
        ]);
      }
      media.classList.add('is-ready');
    }

    // La foto grande que enseña la columna al abrir: la del producto pedido o, si
    // no, la del que marca Liquid con data-stl-current. En "cards" solo hay una.
    mainImage(root, handle = null) {
      const product =
        (handle && root.querySelector(`.stl-media__product[data-stl-media-for="${handle}"]`)) ||
        root.querySelector('.stl-media__product[data-stl-current]');
      return (product || root).querySelector('[data-stl-main-image]');
    }

    // Tras la precarga, la foto con la que abrirá el look, para que al montarla
    // salga de la caché del navegador. Mismos srcset y sizes que el <img>, o el
    // navegador podría elegir otro candidato y bajar un fichero distinto.
    warmMainImage(content, opener) {
      if (!this.mediaSlot?.getClientRects().length) return;
      const picked = opener.querySelector('[data-stl-product]:hover')?.dataset.stlProduct || null;
      const image = this.mainImage(content, picked);
      if (!image) return;

      const preload = new Image();
      // sizes before srcset: the candidate is resolved as soon as srcset is assigned.
      preload.sizes = image.getAttribute('sizes') || MEDIA_SIZES;
      const srcset = image.getAttribute('srcset');
      if (srcset) preload.srcset = srcset;
      preload.src = image.getAttribute('src');
    }

    /**
     * Switches the drawer to another product of the look. Media block, buy block and
     * strip button are marked in lockstep with [data-stl-current]; the CSS shows only
     * the current one. Everything is already in the DOM, so there is no fetch here.
     */
    selectProduct(handle) {
      if (!handle) return;

      const mark = (nodes, isCurrent) => {
        nodes.forEach((el) => {
          if (isCurrent(el)) el.setAttribute('data-stl-current', '');
          else el.removeAttribute('data-stl-current');
        });
      };

      const media = Array.from(this.querySelectorAll('.stl-media__product'));
      const buys = Array.from(this.querySelectorAll('[data-stl-buy]'));
      const btns = Array.from(this.querySelectorAll('[data-stl-select]'));
      // An unknown handle would blank the drawer; keep the current selection instead.
      const known =
        media.some((el) => el.dataset.stlMediaFor === handle) ||
        buys.some((el) => el.dataset.productHandle === handle);
      if (!known) return;

      mark(media, (el) => el.dataset.stlMediaFor === handle);
      mark(buys, (el) => el.dataset.productHandle === handle);
      mark(btns, (el) => el.dataset.stlSelect === handle);
      btns.forEach((el) => {
        if (el.dataset.stlSelect === handle) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });

      // Solo del producto visible: traer los 7 de golpe sería una petición por
      // producto para markup que casi nunca se llega a ver.
      this.guidePending = this.loadSizeGuide(handle);
    }

    /**
     * Guía de tallas y tallaje del producto activo, recortados de su PDP y
     * volcados en los destinos propios del drawer: las clases
     * .quick-add-drawer__* pertenecen a la sheet global y se resuelven con
     * document.querySelector, así que reutilizarlas la rompería.
     *
     * La guía son las dos piezas que emite snippets/size-guide.liquid: el
     * <modal-opener> que se ve en el bloque de compra y el <modal-dialog> que
     * abre. Antes se recortaban por su envoltorio #size_guide_container, que el
     * rediseño de la PDP eliminó (476eb550) — de ahí que no llegara nada.
     */
    async loadSizeGuide(handle) {
      const block = this.querySelector(`[data-stl-buy][data-product-handle="${handle}"]`);
      const guideTarget = block?.querySelector('[data-stl-size-guide]');
      if (!guideTarget || guideTarget.dataset.stlLoaded === 'true') return;

      try {
        let html = this.sizeGuideCache.get(handle);
        if (!html) {
          const res = await fetch(`${this.rootBase()}products/${handle}`);
          if (!res.ok) return;
          html = await res.text();
          this.sizeGuideCache.set(handle, html);
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Solo el diálogo: el enlace que lo abre lo emite ya el propio bloque
        // (snippets/stl-buy-block.liquid), para que no aparezca a media apertura.
        const modal = doc.querySelector('modal-dialog.brn-modal--size-guide');
        if (modal) {
          this.clearSizeGuides(guideTarget);

          // Se monta en un div suelto para poder marcarlo y renombrarlo ANTES de
          // que se conecte: <modal-dialog> se muda solo a <body> al conectarse
          // (assets/base.js), y una vez allí ya no cuelga de este destino.
          const holder = document.createElement('div');
          holder.innerHTML = modal.outerHTML;
          holder.querySelectorAll('modal-dialog').forEach((el) => {
            el.setAttribute('data-stl-guide', '');
            el.id = GUIDE_MODAL_ID;
          });
          guideTarget.replaceChildren(...holder.childNodes);
          guideTarget.dataset.stlLoaded = 'true';

          // size-chart.js engancha la animación del diagrama recorriendo los
          // [data-size-guide-trigger] que hay en ese momento, y este acaba de
          // llegar: sin volver a llamar, el click no levanta la reja del CSS
          // (.size-guide-modal__diagram--visible) y las flechas de medidas se
          // quedan ocultas y sin animar. Marca los ya enganchados, así que
          // repetirlo no duplica nada. Mismo remate que en quick-add-cart.js.
          window.sizeChartHelpers?.initSizeGuideDiagramAnimation?.();
        }

        // La tabla la rellena size-chart.js leyendo el [data-size-guide-source]
        // que viaja dentro del modal. Se le pasa el modal recién montado como
        // ámbito: por defecto usa el primer .size-chart del documento, que con
        // la cesta llena es el de la sheet de tallas del carrito.
        const liveModal = document.getElementById(GUIDE_MODAL_ID);
        if (liveModal) {
          window.sizeChartHelpers?.initSizeChartTabsFromSource?.(liveModal);
          window.sizeChartHelpers?.buildSizeChartPanesFromSource?.(liveModal);
          window.sizeChartHelpers?.initSizeChartTabs?.(liveModal);
        }

        // El de la PDP, no el aviso de "sin devolución" de buy-buttons.liquid
        // ni el de la sheet de tallas, que comparten clase.
        const sizing =
          doc.querySelector('.brn-product__info .brn-product__sizing:not(.no_returns-sizing)') ||
          doc.querySelector('.brn-product__sizing:not(.no_returns-sizing)');
        const sizingTarget = block.querySelector('[data-stl-sizing]');
        if (sizing && sizingTarget) sizingTarget.innerHTML = sizing.innerHTML;
      } catch (error) {
        // Sin guía de tallas se puede comprar igual; no se bloquea el drawer.
        console.warn('[shop-the-look] could not load size guide', handle, error);
      }
    }

    /**
     * El <modal-opener> del enlace abre la guía por su cuenta en cuanto está
     * montada —resuelve su data-modal en el propio click—, así que esto solo
     * cubre el hueco: pulsarlo antes de que caiga el fetch no haría nada, y aquí
     * se espera a que termine y se abre. Sin guía para ese producto se queda
     * como está, sin abrir nada.
     */
    async openSizeGuide(button) {
      if (document.getElementById(GUIDE_MODAL_ID)) return;
      await this.guidePending;
      // Se repite el click en vez de llamar a show(): así lo recogen todos los
      // que escuchan al botón —el <modal-opener>, que abre, y size-chart.js, que
      // arranca la animación del diagrama—, que se engancharon mientras llegaba.
      // La segunda vuelta por aquí sale por el early return de arriba.
      if (document.getElementById(GUIDE_MODAL_ID)) button.click();
    }

    /**
     * Solo puede haber una guía viva a la vez: todas se montan bajo el mismo
     * GUIDE_MODAL_ID —al que apuntan los enlaces de todos los bloques— y
     * size-chart.js resuelve además .size-chart y [data-size-guide-source] con
     * document.querySelector. Con dos montadas, la segunda abriría la tabla de
     * la primera.
     */
    clearSizeGuides(keep) {
      this.querySelectorAll('[data-stl-size-guide][data-stl-loaded="true"]').forEach((el) => {
        if (el === keep) return;
        el.replaceChildren();
        delete el.dataset.stlLoaded;
      });
      document.querySelectorAll('modal-dialog[data-stl-guide]').forEach((el) => el.remove());
    }

    // El primero del look: los bloques de compra salen en el mismo orden que la
    // tira y que las miniaturas de la tarjeta (snippets/look-products.liquid),
    // así que el primero del DOM es el que Liquid marca con index 0.
    firstProductHandle() {
      return this.querySelector('[data-stl-buy]')?.dataset.productHandle || null;
    }

    rootBase() {
      const root = this.dataset.rootUrl || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    /**
     * Las tallas son radios (mismo markup que brn-sheet-size en la PDP), así que
     * el estado marcado lo lleva el navegador; aquí solo se arma el botón con la
     * variante elegida.
     *
     * En móvil el bloque de compra ES la sheet de tallas, y allí no hay botón
     * (se oculta en _shop-the-look.scss): pulsar la talla añade directamente,
     * como en la sheet de la PDP. Las agotadas van disabled, así que no llegan
     * aquí. Se sigue armando el botón antes de añadir para que un giro a
     * horizontal lo encuentre listo.
     */
    onChange(e) {
      const input = e.target.closest('[data-stl-size]');
      if (!input || !this.contains(input)) return;

      const block = input.closest('[data-stl-buy]');
      const add = block?.querySelector('[data-stl-add]');
      // Se arma igual el botón: es la única vía en el producto sin opciones,
      // donde no hay talla que pulsar.
      if (add) {
        add.dataset.stlVariant = input.dataset.stlSize;
        add.disabled = false;
      }

      if (this.isMobile()) this.addToCart(input.dataset.stlSize, block?.dataset.productId);
    }

    // Mismo corte que el @media de _shop-the-look.scss ($br-m: 1024px), leído en
    // cada uso para que un resize o un giro de pantalla no deje el criterio
    // congelado en el de la carga.
    isMobile() {
      return window.matchMedia('(max-width: 1023.98px)').matches;
    }

    /**
     * El alta la hace quick-add-cart.js, que es el que manda las secciones del
     * carrito, refresca el contador de la cabecera, avisa por PUB_SUB —de ahí
     * cuelga el remove-on-add de la wishlist— y abre el minicart. Tener aquí una
     * copia era justo lo que hacía que comprar desde shop the look se comportara
     * distinto al resto del sitio.
     */
    addToCart(variantId, productId) {
      if (!variantId || typeof window.quickAddCart?.addToCart !== 'function') return;
      window.quickAddCart.addToCart(variantId, { productId });
      // La sheet de cards se retira sin esperar al alta, igual que la global: el
      // minicart es el que confirma y dejarla abierta lo taparía. El panel de
      // "buy" no es una capa aparte, así que ahí no hay nada que cerrar.
      this.closeSheet();
    }

    sheetIsOpen() {
      return this.sheet?.getAttribute('aria-hidden') === 'false';
    }

    /**
     * Monta la sheet de tallas sobre el panel de cards, en el producto pedido. La
     * llama quick-add-cart.js en lugar de abrir la suya (ver openLookSheet allí),
     * y devuelve false si este look no trae bloque para ese producto —el caso de
     * la variante "buy", donde los bloques están en el panel—, para que aquel
     * siga con la sheet global en vez de dejar la card sin respuesta.
     *
     * No pide nada: los bloques de todos los productos del look llegaron con la
     * respuesta de la sección.
     */
    openSizeSheet(handle) {
      if (!handle || !this.sheet || !this.sheetContent) return false;

      const blocks = Array.from(this.sheetContent.querySelectorAll('[data-stl-buy]'));
      const block = blocks.find((el) => el.dataset.productHandle === handle);
      if (!block) return false;

      blocks.forEach((el) => {
        if (el === block) el.setAttribute('data-stl-current', '');
        else el.removeAttribute('data-stl-current');
      });

      // Sin talla marcada de una apertura anterior: la sheet de la PDP también se
      // abre en blanco, y aquí dejarla marcada equivaldría a haberla pulsado.
      block.querySelectorAll('[data-stl-size]:checked').forEach((input) => {
        input.checked = false;
      });

      this.sheet.setAttribute('aria-hidden', 'false');
      document.body.classList.add(SHEET_OPEN_CLASS);
      // Se guarda la promesa para el click temprano; ver openSizeGuide.
      this.guidePending = this.loadSizeGuide(handle);
      return true;
    }

    closeSheet() {
      if (!this.sheet || !this.sheetIsOpen()) return;
      this.sheet.setAttribute('aria-hidden', 'true');
      document.body.classList.remove(SHEET_OPEN_CLASS);
    }

    // Hovering (or tabbing to) a thumb is the earliest hint that its image is about to be
    // needed; warming it there covers the case where the idle pass has not run yet.
    onPointerOver(e) {
      const thumb = e.target.closest?.('[data-stl-thumb]');
      if (thumb) this.warmThumb(thumb);
    }

    /**
     * The full-size image behind each thumb is a file the visitor is very likely to ask
     * for, and starting its download only on the click is what made the swap feel slow.
     * Warming them once the content is in place means selectThumb hits the browser cache.
     * Skipped when the media column is not rendered (mobile, where it is display:none), so
     * it costs nothing there.
     */
    warmMedia() {
      if (!this.mediaSlot || !this.mediaSlot.getClientRects().length) return;

      const thumbs = this.mediaSlot.querySelectorAll('[data-stl-thumb]');
      if (!thumbs.length) return;

      const warm = () => thumbs.forEach((thumb) => this.warmThumb(thumb));
      // Behind the lead image and the cards' own requests, never competing with them.
      if (window.requestIdleCallback) window.requestIdleCallback(warm, { timeout: 1500 });
      else setTimeout(warm, 300);
    }

    warmThumb(thumb) {
      const { full, srcset, stlWarm } = thumb.dataset;
      if (!full || stlWarm) return;
      thumb.dataset.stlWarm = '1';

      const preload = new Image();
      // sizes before srcset: the candidate is resolved as soon as srcset is assigned.
      preload.sizes = MEDIA_SIZES;
      if (srcset) preload.srcset = srcset;
      preload.src = full;
    }

    selectThumb(thumb) {
      // Scoped to the thumb's own product: the drawer holds one media block per
      // product of the look, so a document-wide lookup would hit the wrong image.
      const scope = thumb.closest('.stl-media__product') || this;
      const main = scope.querySelector('[data-stl-main-image]');
      if (!main || !thumb.dataset.full) return;

      // Solo los looks de sección pueden traer vídeos en la columna: uno por
      // ranura, encima del marco, y se enseña el de la miniatura pulsada. En las
      // otras dos variantes esto no encuentra ninguno y no hace nada. La foto
      // grande se cambia igualmente, aunque quede debajo del vídeo: así volver a
      // una miniatura de foto no espera a ninguna descarga.
      const videos = scope.querySelectorAll('[data-stl-video]');
      if (videos.length) {
        const target = thumb.dataset.stlVideo;
        videos.forEach((video) => {
          const isCurrent = target !== undefined && video.dataset.stlVideo === target;
          video.hidden = !isCurrent;
          if (isCurrent) video.play?.().catch(() => {});
          else video.pause?.();
        });
        main.hidden = target !== undefined;
      }

      if (thumb.dataset.srcset) {
        main.sizes = MEDIA_SIZES;
        main.srcset = thumb.dataset.srcset;
      } else {
        main.removeAttribute('srcset');
      }
      main.src = thumb.dataset.full;
      if (thumb.dataset.alt) main.alt = thumb.dataset.alt;

      thumb
        .closest('.stl-media__thumbs')
        ?.querySelectorAll('.stl-media__thumb')
        .forEach((el) => el.classList.toggle('is-active', el === thumb));
    }
  }

  if (!customElements.get('shop-the-look-drawer')) {
    customElements.define('shop-the-look-drawer', ShopTheLookDrawer);
  }
})();
